import DOMPurify from 'dompurify';

import { formatDateTime } from './index';

const hoursFromNow = (hours) => new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();

export const templatePreviewDefaults = [
  ['order.id', 10482],
  ['order.pickupName', 'María González'],
  ['order.pickupPhone', '+56912345678'],
  ['order.pickupEmail', 'maria.gonzalez@correo.cl'],
  ['order.pickupNationalId', '12.345.678-9'],
  ['order.pickupCode', '482915'],
  ['order.dropCode', '739201'],
  ['order.courierPickupCode', '551028'],
  ['order.status', 'ready'],
  ['order.orderType', 'delivery'],
  ['order.autoReversalAt', hoursFromNow(48)],
  ['order.reservedAt', hoursFromNow(-2)],
  ['order.readyAt', hoursFromNow(-26)],
  ['order.after3d', hoursFromNow(24)],
  ['order.after4d', hoursFromNow(48)],
  ['order.extras.idOrden', 'ORD-10482'],
  ['order.extras.item', 'Medicamentos'],
  ['locker.id', 21],
  ['locker.name', 'Locker Providencia'],
  ['locker.locationName', 'Mall Costanera'],
  ['location.id', 8],
  ['location.name', 'Mall Costanera'],
  ['location.address', 'Av. Andrés Bello 2425, Providencia'],
  ['location.description', 'Nivel -1, junto a la entrada del estacionamiento.'],
  ['location.lat', -33.4178],
  ['location.lon', -70.6065],
  ['door.doorNumber', 12],
  ['user.name', 'Juan Pérez'],
  ['user.email', 'juan.perez@correo.cl'],
  ['user.phone', '+56987654321'],
  ['courier.name', 'Ana Soto'],
  ['courier.email', 'ana.soto@correo.cl'],
  ['courier.phone', '+56911112222'],
  ['client.name', 'Cliente'],
  ['client.supportEmail', 'soporte@cliente.cl'],
  ['client.supportPhone', '+56 2 2964 2430'],
  ['client.fromName', 'Cliente'],
  ['client.fromEmail', 'notificaciones@cliente.cl'],
  ['name', 'Juan Pérez'],
  ['pickupName', 'María González'],
  ['token', 'CUST-482915'],
  ['barcodeGeneratorEndpoint', 'https://api.elocker.cl/barcode'],
  ['now', new Date().toISOString()],
  ['prefs.supportPhone', '+56 2 2964 2430'],
  ['prefs.supportEmail', 'soporte@cliente.cl'],
];

const setPreviewPath = (target, path, value) => {
  const keys = path.split('.');
  let cursor = target;
  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    if (!cursor[key] || typeof cursor[key] !== 'object') cursor[key] = {};
    cursor = cursor[key];
  });
};

const previewValue = (context, path) => {
  const key = String(path || '').trim();
  if (!key || key === 'now') return context && context.now;
  return key.split('.').reduce((current, part) => (current == null ? current : current[part]), context);
};

const previewTruthy = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return value != null && value !== '' && value !== false;
};

const asDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const countBetween = (args, context, unitMs) => {
  const dates = args.map((arg) => asDate(previewValue(context, arg))).filter(Boolean);
  if (dates.length < 2) return '2';
  return String(Math.max(1, Math.round(Math.abs(dates[1] - dates[0]) / unitMs)));
};

const previewHelper = (name, args, context) => {
  if (name === 'formatDate') {
    return formatDateTime(previewValue(context, args[0]), 'D [de] MMMM [de] YYYY, HH:mm') || '';
  }
  if (name === 'mapUrl') {
    const value = previewValue(context, args[0]);
    return value ? `https://maps.google.com/?q=${encodeURIComponent(String(value))}` : '';
  }
  if (name === 'countDays') return countBetween(args, context, 24 * 60 * 60 * 1000);
  if (name === 'countHours') return countBetween(args, context, 60 * 60 * 1000);
  const resolved = args.map((arg) => previewValue(context, arg)).find((value) => value != null && value !== '');
  return resolved == null ? '' : String(resolved);
};

const tokenize = (source) => {
  const tokens = [];
  const re = /\{\{\{([\s\S]*?)\}\}\}|\{\{([\s\S]*?)\}\}/g;
  let last = 0;
  let match = re.exec(source);
  while (match) {
    if (match.index > last) tokens.push({ type: 'text', value: source.slice(last, match.index) });
    tokens.push({
      type: 'tag',
      raw: String(match[1] != null ? match[1] : match[2]).trim(),
      token: match[0],
    });
    last = match.index + match[0].length;
    match = re.exec(source);
  }
  if (last < source.length) tokens.push({ type: 'text', value: source.slice(last) });
  return tokens;
};

const opensBlock = (raw) => raw.startsWith('#if') || raw.startsWith('#with') || raw.startsWith('^');

const splitBlock = (tokens, start) => {
  const whenTrue = [];
  const whenFalse = [];
  let inElse = false;
  let depth = 1;
  let index = start;
  while (index < tokens.length && depth > 0) {
    const token = tokens[index];
    if (token.type === 'tag' && opensBlock(token.raw)) {
      depth += 1;
      (inElse ? whenFalse : whenTrue).push(token);
    } else if (token.type === 'tag' && token.raw.startsWith('/')) {
      depth -= 1;
      if (depth === 0) {
        index += 1;
        break;
      }
      (inElse ? whenFalse : whenTrue).push(token);
    } else if (token.type === 'tag' && token.raw === 'else' && depth === 1) {
      inElse = true;
    } else {
      (inElse ? whenFalse : whenTrue).push(token);
    }
    index += 1;
  }
  return { whenTrue, whenFalse, next: index };
};

const renderTag = (token, context) => {
  const raw = token.raw;
  if (!raw || raw.startsWith('#') || raw.startsWith('/') || raw.startsWith('^') || raw === 'else') return '';
  const parts = raw.split(/\s+/).filter(Boolean);
  const resolved = parts.length > 1 ? previewHelper(parts[0], parts.slice(1), context) : previewValue(context, raw);
  return resolved == null ? '' : String(resolved);
};

const renderTokens = (tokens, context) => {
  let output = '';
  let index = 0;
  while (index < tokens.length) {
    const token = tokens[index];
    if (token.type === 'text') {
      output += token.value;
      index += 1;
      continue;
    }
    const raw = token.raw;
    if (raw.startsWith('#if')) {
      const path = raw.slice(3).trim();
      const block = splitBlock(tokens, index + 1);
      const chosen = previewTruthy(previewValue(context, path)) ? block.whenTrue : block.whenFalse;
      output += renderTokens(chosen, context);
      index = block.next;
      continue;
    }
    if (raw.startsWith('#with')) {
      const path = raw.slice(5).trim();
      const block = splitBlock(tokens, index + 1);
      const scoped = previewValue(context, path);
      const child = scoped && typeof scoped === 'object' ? { ...context, ...scoped } : context;
      output += renderTokens(block.whenTrue, child);
      index = block.next;
      continue;
    }
    if (raw.startsWith('^')) {
      const path = raw.slice(1).trim();
      const block = splitBlock(tokens, index + 1);
      if (!previewTruthy(previewValue(context, path))) output += renderTokens(block.whenTrue, context);
      index = block.next;
      continue;
    }
    output += renderTag(token, context);
    index += 1;
  }
  return output;
};

export const renderNotificationTemplate = (source, context) => {
  if (!source) return '';
  return renderTokens(tokenize(String(source)), context || {});
};

export const buildTemplatePreviewContext = (clientData) => {
  const context = {};
  templatePreviewDefaults.forEach(([path, value]) => setPreviewPath(context, path, value));
  const data = clientData && clientData.toJS ? clientData.toJS() : clientData || {};
  const preferences = data.preferences || {};
  const actualClient = {
    id: data.id,
    name: data.name,
    supportEmail: preferences.supportEmail,
    supportPhone: preferences.supportPhone,
    fromName: preferences.fromName,
    fromEmail: preferences.fromEmail,
  };
  Object.keys(actualClient).forEach((key) => {
    if (actualClient[key] != null && actualClient[key] !== '') context.client[key] = actualClient[key];
  });
  context.client.preferences = preferences;
  context.prefs = {
    ...context.prefs,
    ...preferences,
    supportEmail: context.client.supportEmail,
    supportPhone: context.client.supportPhone,
  };
  context.now = new Date().toISOString();
  return context;
};

const escapePreviewHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const markdownToPreviewHtml = (source) => {
  const html = escapePreviewHtml(source)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/^###### (.+)$/gm, '<h6>$1</h6>')
    .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
    .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>');
  return `<div>${html.replace(/\n{2,}/g, '<br/><br/>').replace(/\n/g, '<br/>')}</div>`;
};

const previewMarkup = (source, context) => {
  const rendered = renderNotificationTemplate(source, context);
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(rendered);
  if (looksLikeHtml) return rendered;
  return markdownToPreviewHtml(rendered);
};

export const previewEmailHtml = (source, context) =>
  DOMPurify.sanitize(previewMarkup(source, context), { ADD_ATTR: ['target'] });

const frameChrome =
  '<style data-frame-chrome="true">html,body{margin:0;padding:16px;background:#fff;color:#1c2833;font:15px/1.5 Helvetica,Arial,sans-serif;}img{max-width:100%;height:auto;}</style>';

export const templateFrameDocument = (markup) => {
  const raw = String(markup || '');
  if (/<html[\s>]/i.test(raw)) return raw;
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${frameChrome}</head><body>${raw}</body></html>`;
};

export const previewFrameDocument = (source, context) => templateFrameDocument(previewMarkup(source, context));
